import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Dialog,
  Button,
  Select,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { usePermission } from 'src/hooks/usePermission';

import Iconify from 'src/components/iconify';

export default function RoleArchiveDialog({
  open,
  role,
  availableRoles,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [targetRoleId, setTargetRoleId] = useState('');

  const canArchive = can({ permission: 'ROLES:DELETE' });

  /**
   * ROLE FORMATTING ENGINE
   * Cleans technical keys for selection visibility.
   */
  const formatRoleName = (name) => {
    if (!name) return '';
    return name
      .replace('ROLE_', '')
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (!role) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 27, 34, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <Iconify
          icon="solar:trash-bin-trash-bold-duotone"
          color="error.main"
          width={24}
        />
        {t('rolesPage.deactivate')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}
            // Renders the warning sentence and applies the bold formatting from your JSON files
            dangerouslySetInnerHTML={{
              __html: t('rolesPage.archiveModal.warningMessage', {
                roleName: formatRoleName(role.roleName),
                userCount: role.userCount,
              }),
            }}
          />

          <FormControl fullWidth disabled={!canArchive}>
            <InputLabel id="migration-role-label">
              {t('rolesPage.archiveModal.fallbackRoleLabel')}
            </InputLabel>
            <Select
              labelId="migration-role-label"
              label={t('rolesPage.archiveModal.fallbackRoleLabel')}
              value={targetRoleId}
              onChange={(e) => setTargetRoleId(e.target.value)}
              sx={{ borderRadius: '12px' }}
            >
              {availableRoles
                .filter((r) => r.id !== role.id && (r.active || r.isActive))
                .map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {formatRoleName(r.roleName)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700 }}>
          {t('buttons.cancel')}
        </Button>
        <Button
          onClick={() => onConfirm(targetRoleId)}
          variant="contained"
          color="error"
          disabled={!targetRoleId || !canArchive} // MASKED
          sx={{
            borderRadius: '12px',
            fontWeight: 800,
            boxShadow: '0 8px 16px rgba(255, 72, 66, 0.24)',
          }}
        >
          {t('rolesPage.archiveModal.reassignAndArchive')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RoleArchiveDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  role: PropTypes.object,
  availableRoles: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
